<template>
  <div class="chapter-list-panel">
    <div class="chapter-header">
      <div><span class="nr-kicker">CONTENTS</span><h3>章节目录</h3></div>
      <button class="panel-close" aria-label="关闭目录" @click="$emit('close')"><nricon name="close" /></button>
    </div>
    
    <div class="chapter-search">
      <div class="ft-search">
        <nricon name="search" />
        <input
          v-model="searchText"
          class="ft-input"
          placeholder="搜索章节标题，回车全文搜索"
          @keyup.enter="fullTextSearch"
        >
        <button class="btn btn--sm ft-btn" @click="fullTextSearch">
          <nricon v-if="searching" name="loading" />
          <span v-else>全文</span>
        </button>
      </div>
    </div>

    <div class="chapter-body">
      <!-- 全文搜索结果（命中数有限，正常渲染） -->
      <div v-if="mode === 'fulltext'" class="ft-scroll" ref="content">
        <div class="ft-hint">
          命中 {{ fullResults.length }} 章
          <button class="ft-back" @click="exitFullText">返回目录</button>
        </div>
        <button
          v-for="r in fullResults"
          :key="r.chapterId"
          class="chapter-item ft-item"
          :class="{ active: currentChapterId === r.chapterId }"
          @click="handleSelectById(r.chapterId)"
        >
          <div class="ft-title">{{ r.title }}</div>
          <div class="ft-snippet" v-html="highlight(r.snippet)"></div>
        </button>
        <div v-if="!fullResults.length" class="ft-empty">未找到匹配内容</div>
      </div>

      <!-- 目录：虚拟滚动，只渲染可视区，支持上万章不卡 -->
      <virtual-list v-else ref="vlist" :items="filteredChapters" :item-height="44">
        <template #default="{ item }">
          <button
            class="chapter-item toc-item"
            :class="{ active: currentChapterId === item.id }"
            @click="handleSelect(item)"
          >{{ item.title }}</button>
        </template>
      </virtual-list>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import VirtualList from '@/components/VirtualList.vue'

export default {
  name: 'ChapterList',
  components: { VirtualList },
  props: {
    chapters: {
      type: Array,
      default: () => []
    },
    currentChapterId: {
      type: [String, Number],
      default: null
    },
    bookId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      searchText: '',
      mode: 'toc', // toc | fulltext
      fullResults: [],
      searching: false
    }
  },
  computed: {
    filteredChapters() {
      if (!this.searchText) return this.chapters
      const searchLower = this.searchText.toLowerCase()
      return this.chapters.filter(chapter => 
        chapter.title.toLowerCase().includes(searchLower)
      )
    }
  },
  mounted() {
    this.$nextTick(this.scrollToActive)
  },
  watch: {
    currentChapterId() {
      this.$nextTick(this.scrollToActive)
    },
    chapters() {
      this.$nextTick(this.scrollToActive)
    },
    searchText() {
      this.$nextTick(this.scrollToActive)
    }
  },
  methods: {
    handleSelect(chapter) {
      this.$emit('select', chapter, true)
      this.$emit('close')
    },
    async fullTextSearch() {
      const kw = this.searchText.trim()
      if (!kw) {
        this.exitFullText()
        return
      }
      if (!this.bookId) return
      this.searching = true
      try {
        const { data } = await api.book.searchInBook(this.bookId, kw)
        this.fullResults = data
        this.mode = 'fulltext'
      } catch (e) {
        this.$message.error('全文搜索失败')
      } finally {
        this.searching = false
      }
    },
    exitFullText() {
      this.mode = 'toc'
      this.fullResults = []
    },
    handleSelectById(chapterId) {
      const chapter = this.chapters.find(c => c.id === chapterId)
      if (chapter) {
        // 带上搜索词，阅读器据此在正文里高亮命中并滚动定位
        this.$emit('select', chapter, true, this.searchText.trim())
        this.$emit('close')
      }
    },
    // 转义后高亮关键词，供 v-html 使用（避免 XSS）
    highlight(text) {
      if (!text) return ''
      const esc = s => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
      const safe = esc(text)
      const kw = this.searchText.trim()
      if (!kw) return safe
      const kwEsc = esc(kw).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return safe.replace(new RegExp(kwEsc, 'gi'), m => `<mark>${m}</mark>`)
    },
    scrollToActive() {
      if (this.mode !== 'toc') return
      const idx = this.filteredChapters.findIndex(c => c.id === this.currentChapterId)
      if (idx < 0) return
      const vlist = this.$refs.vlist
      if (vlist && vlist.scrollToIndex) vlist.scrollToIndex(idx, 'center')
    }
  }
}
</script>

<style lang="scss" scoped>
.chapter-list-panel {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 300px;
  background: #fff;
  box-shadow: 2px 0 8px rgba(0,0,0,0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
}

.chapter-header {
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  
  h3 {
    margin: 0;
  }
  
  i {
    cursor: pointer;
    font-size: 20px;
  }
}

.chapter-search {
  padding: 10px;
  border-bottom: 1px solid var(--nr-border);
}

.ft-search {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 6px 0 10px;
  border: 1px solid var(--nr-border);
  border-radius: var(--nr-radius);
  background: var(--nr-surface);

  > i { color: var(--nr-text-3); font-size: 15px; }

  .ft-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: var(--nr-text);
    &::placeholder { color: var(--nr-text-3); }
  }

  .ft-btn { flex-shrink: 0; height: 28px; padding: 0 10px; }
}

.chapter-body {
  flex: 1;
  min-height: 0;
  position: relative;
}

.ft-scroll {
  height: 100%;
  overflow-y: auto;
  padding: 10px;

  // 隐藏滚动条（该抽屉仅移动端使用）：不占宽度，与主阅读区一致
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
  }
}

.chapter-item {
  padding: 10px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: #f5f7fa;
  }

  &.active {
    background: #ecf5ff;
    color: #409eff;
  }
}

// 目录行：定高单行，配合虚拟滚动
.toc-item {
  height: 44px;
  line-height: 44px;
  padding: 0 12px;
  border-radius: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ft-hint {
  padding: 6px 10px;
  font-size: 12px;
  color: #909399;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .ft-back {
    color: #409eff;
    cursor: pointer;
  }
}

.ft-item {
  .ft-title {
    font-size: 14px;
    margin-bottom: 4px;
  }
  .ft-snippet {
    font-size: 12px;
    color: #909399;
    line-height: 1.5;

    ::v-deep mark {
      background: #fff3a3;
      color: inherit;
      padding: 0 1px;
      border-radius: 2px;
    }
  }
}

.ft-empty {
  padding: 20px;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}
</style> 

<style lang="scss" scoped>
.chapter-list-panel {
  width: min(380px, 92vw);
  color: var(--nr-text);
  border-right: 1px solid var(--nr-border);
  background: var(--nr-surface);
  box-shadow: var(--nr-shadow-float);
}

.chapter-header {
  min-height: 86px;
  padding: 18px 22px;
  border-bottom-color: var(--nr-border);
  h3 { margin-top: 4px; font-family: var(--nr-font-display); font-size: 23px; font-weight: 600; }
}

.panel-close {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  color: var(--nr-text-3);
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  &:hover { color: var(--nr-text); background: var(--nr-surface-2); }
}

.chapter-search { padding: 15px 18px; }
.ft-search {
  height: 46px;
  padding: 0 6px 0 14px;
  border-color: var(--nr-border-strong);
  border-radius: 12px;
  background: var(--nr-bg);
  &:focus-within { border-color: var(--nr-accent); box-shadow: var(--nr-ring); }
  .ft-input { font-size: 13px; }
  .ft-btn { min-height: 34px; height: 34px; border-color: transparent; background: var(--nr-surface); }
}

.toc-item,
.chapter-item {
  width: calc(100% - 24px);
  margin-inline: 12px;
  color: var(--nr-text-2);
  border: 0;
  border-bottom: 1px solid color-mix(in srgb, var(--nr-border) 70%, transparent);
  border-radius: 0;
  background: transparent;
  font: 13px var(--nr-font-body);
  text-align: left;
  transition: color 160ms ease, background 160ms ease;

  &:hover { color: var(--nr-text); background: var(--nr-surface-2); }
  &.active { color: var(--nr-accent-text); background: var(--nr-accent-bg); font-weight: 600; }
}

.toc-item { position: relative; padding: 0 14px 0 24px; }
.toc-item::before { content: ''; position: absolute; left: 10px; top: 20px; width: 4px; height: 4px; border-radius: 50%; background: var(--nr-border-strong); }
.toc-item.active::before { background: var(--nr-accent); }

.ft-scroll { padding: 10px 6px 24px; }
.ft-hint { padding: 6px 16px 13px; color: var(--nr-text-3); }
.ft-hint .ft-back { min-height: 34px; padding: 0 8px; color: var(--nr-accent-text); border: 0; border-radius: 7px; background: transparent; font-size: 12px; cursor: pointer; }
.ft-hint .ft-back:hover { background: var(--nr-accent-bg); }
.ft-item { display: block; height: auto; min-height: 72px; padding: 12px 14px; }
.ft-item .ft-title { color: var(--nr-text); font-family: var(--nr-font-display); font-size: 15px; font-weight: 600; }
.ft-item .ft-snippet { color: var(--nr-text-3); }
.ft-empty { color: var(--nr-text-3); }

@media (max-width: 480px) {
  .chapter-list-panel { width: 100%; }
  .chapter-header { min-height: 76px; padding-top: max(14px, env(safe-area-inset-top)); }
}

// 阅读页移动端目录：底部抽屉形态（与参考站 wap 目录浮层一致）
.is-mobile {
  .chapter-list-panel {
    top: auto;
    width: 100%;
    height: min(78dvh, 640px);
    border-right: 0;
    border-top: 1px solid var(--nr-border);
    border-radius: 18px 18px 0 0;
    box-shadow: 0 -8px 32px rgba(0, 0, 0, .18);
  }
  .chapter-header {
    position: relative;
    min-height: 62px;
    padding: 14px 18px;
    // 抽屉提手
    &::before {
      content: '';
      position: absolute;
      top: 7px;
      left: 50%;
      width: 36px;
      height: 4px;
      margin-left: -18px;
      border-radius: 2px;
      background: var(--nr-border-strong);
    }
    h3 { margin-top: 4px; font-size: 16px; }
  }
  .chapter-search { padding: 10px 14px; }
  .ft-search { height: 40px; }
  .chapter-body { padding-bottom: env(safe-area-inset-bottom); }
}
</style>
